import Vue from 'vue';

export const setProjectPercentageValue = ({row, projectId, value}) => {
   Vue.set(row, 'projectsData', {...row.projectsData, [String(projectId)]: value});
};

/**
 * Ensures a row has a reactive percentage object.
 * @param {Object} row
 * @returns {void}
 */
export const ensurePercentageField = (row) => {
   if (!row.percentage) {
      Vue.set(row, 'percentage', {});
   }
};

/**
 * Sets percentage value and updates lastModified timestamp.
 * @param {{row: Object, value: number|null}} params
 * @returns {void}
 */
export const setPercentageWithTimestamp = ({row, value}) => {
   ensurePercentageField(row);
   Vue.set(row.percentage, 'value', value);
   Vue.set(row.percentage, 'lastModified', new Date().toISOString());
};

/**
 * Ensures a row has a reactive projectsData object and returns it.
 * @param {Object} row
 * @returns {Object<string, *>}
 */
export const ensureProjectsDataField = (row) => {
   if (!row.projectsData) {
      Vue.set(row, 'projectsData', {});
   }

   return row.projectsData;
};

export const setSingleProjectValue = ({row, projectId, value}) => {
   ensureProjectsDataField(row);
   Vue.set(row.projectsData, projectId, value);
};

export const setProjectsDataWithColumns = ({row, projectsData}) => {
   Vue.set(row, 'projectsData', {...(projectsData || {})});
};

/**
 * Zeros out every project on the row. When `projectIds` isn't supplied, falls back
 * to whatever keys already exist on the row -- but a project the user never
 * touched has no key yet, so that fallback silently skips it. Callers with access
 * to the full project list for the row's period should always pass `projectIds`.
 */
export const zeroProjectsFromAllKnownKeys = (row, projectIds = null) => {
   ensureProjectsDataField(row);
   const ids = projectIds ?? Object.keys(row.projectsData);
   ids.forEach((projectId) => {
      Vue.set(row.projectsData, String(projectId), 0);
   });
};

/**
 * Ensures a row has a reactive activitiesData object and returns it.
 * @param {Object} row
 * @returns {Object<string, *>}
 */
export const ensureActivitiesDataField = (row) => {
   if (!row.activitiesData) {
      Vue.set(row, 'activitiesData', {});
   }

   return row.activitiesData;
};

export const setSingleActivityValue = ({row, activityId, value}) => {
   ensureActivitiesDataField(row);
   Vue.set(row.activitiesData, activityId, value);
};

export const setActivitiesDataWithColumns = ({row, activitiesData}) => {
   Vue.set(row, 'activitiesData', {...(activitiesData || {})});
};

/** Same fallback caveat as zeroProjectsFromAllKnownKeys, for activities. */
export const zeroActivitiesFromAllKnownKeys = (row, activityIds = null) => {
   ensureActivitiesDataField(row);
   const ids = activityIds ?? Object.keys(row.activitiesData);
   ids.forEach((activityId) => {
      Vue.set(row.activitiesData, String(activityId), 0);
   });
};
